import React, { useState, useEffect, useRef } from "react";
import { HiOutlineLocationMarker, HiOutlineMail, HiOutlinePhone, HiOutlineGlobe } from "react-icons/hi";
import "./Contact.css";
import { contactService } from '../services/contactService';
import { supabase } from '../supabaseClient';

function Contact({ edition: customEdition }) {
  const [contactData, setContactData] = useState({
    heroSection: {
      title: 'Contactez-nous',
      subtitle: 'N\'hésitez pas à nous poser vos questions'
    },
    formSection: {
      title: 'Envoyez-nous un message',
      subtitle: 'Nous vous répondrons dans les plus brefs délais',
      submitButtonText: 'Envoyer le message'
    },
    contactInfo: {
      title: 'Informations de contact',
      venue: { title: 'Lieu', address: '', subAddress: '' },
      email: { title: 'Email', primary: '', secondary: '' },
      phone: { title: 'Téléphone', primary: '', secondary: '' },
      social: { title: 'Réseaux sociaux', website: '', handle: '' }
    },
    mapSection: {
      title: 'Localisation',
      description: 'Retrouvez-nous sur la carte',
      coordinates: { lat: 33.5731, lng: -7.5898 },
      popupText: 'Université Hassan II'
    }
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    loadContactData();
  }, [customEdition]);

  const loadContactData = async () => {
    try {
      setLoading(true);
      
      let editionYear = customEdition;
      if (!editionYear) {
        // Get active edition first
        const { data: activeEdition, error: editionError } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();
        editionYear = activeEdition?.year || '2025';
      }

      const data = await contactService.getContactData(editionYear);
      
      if (data) {
        setContactData(data);
      }
      setError(null);
    } catch (error) {
      setError('Failed to load contact data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
  };

  useEffect(() => {
    // Dynamically load Leaflet CSS and JS
    const loadLeaflet = async () => {
      // Load CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);
      }

      // Load JS
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.onload = initMap;
        document.head.appendChild(script);
      } else {
        initMap();
      }
    };

    const initMap = () => {
      if (window.L && mapRef.current && !mapInstanceRef.current) {
        // Use coordinates from context
        const lat = contactData.mapSection.coordinates.lat;
        const lng = contactData.mapSection.coordinates.lng;

        mapInstanceRef.current = window.L.map(mapRef.current).setView([lat, lng], 15);

        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(mapInstanceRef.current);

        // Add marker for the university
        const marker = window.L.marker([lat, lng]).addTo(mapInstanceRef.current);
        marker.bindPopup(contactData.mapSection.popupText).openPopup();
      }
    };

    loadLeaflet();

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [contactData.mapSection]);

  if (loading) {
    return (
      <main className="contact-page">
        <section className="contact-hero">
          <div className="hero-contentcontact">
          {/* Replace text with animated loading spinner */}
              <div className="loading-spinner"></div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-contentcontact">
          <h1>{contactData.heroSection.title}</h1>
          <p>{contactData.heroSection.subtitle}</p>
        </div>
      </section>

      <div className="contact-container">
        {/* Main Contact Section */}
        <section className="main-contact-section">
          <div className="contact-grid">
            {/* Left: Contact Form */}
            <div className="contact-form-section">
              <div className="form-header">
                <h2>{contactData.formSection.title}</h2>
                <p>{contactData.formSection.subtitle}</p>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Nom Complet *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>

                <button type="submit" className="submit-btn">
                  {contactData.formSection.submitButtonText}
                </button>
              </form>
            </div>

            {/* Right: Contact Info */}
            <div className="contact-info-sidebar">
              <h3>{contactData.contactInfo.title}</h3>
              
              <div className="sidebar-info-card">
                <HiOutlineLocationMarker className="sidebar-icon" />
                <div>
                  <h4>{contactData.contactInfo.venue.title}</h4>
                  <p>{contactData.contactInfo.venue.address}<br />{contactData.contactInfo.venue.subAddress}</p>
                </div>
              </div>

              <div className="sidebar-info-card">
                <HiOutlineMail className="sidebar-icon" />
                <div>
                  <h4>{contactData.contactInfo.email.title}</h4>
                  <p>{contactData.contactInfo.email.primary}<br />{contactData.contactInfo.email.secondary}</p>
                </div>
              </div>

              <div className="sidebar-info-card">
                <HiOutlinePhone className="sidebar-icon" />
                <div>
                  <h4>{contactData.contactInfo.phone.title}</h4>
                  <p>{contactData.contactInfo.phone.primary}<br />{contactData.contactInfo.phone.secondary}</p>
                </div>
              </div>

              <div className="sidebar-info-card">
                <HiOutlineGlobe className="sidebar-icon" />
                <div>
                  <h4>{contactData.contactInfo.social.title}</h4>
                  <p>{contactData.contactInfo.social.website}<br />{contactData.contactInfo.social.handle}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="map-section">
          <div className="map-header">
            <h3>{contactData.mapSection.title}</h3>
            <p>{contactData.mapSection.description}</p>
          </div>
          <div className="map-container">
            <div ref={mapRef} className="leaflet-map"></div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Contact;
